import { useExamStepIndustries, useExamStepLearners } from "@/api/services/main/exam-steps";
import { Button } from "@/components/elements/Button";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { InnerBox } from "@/components/elements/InnerBox";
import { Pagination } from "@/components/elements/Pagination";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { usePagination } from "@/hooks/usePagination";
import { ExamLearnerTable } from "@/features/exam-steps/components/ExamLearnerTable";
import { ExamSummarySection } from "@/features/learning-progress/components/exam/ExamSummarySection";
import { divider } from "@/theme/colors";
import { Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { FC, useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import { PROCEDURE_STATUS, TExamLearnersFilterParams } from "@/api/services/main/exam-steps/dtos/get-exam-step-learners.dto";
import { useLearningProgressExamList } from "@/api/services/main/learning-progress";
import { ExamStepsListTop } from "@/features/exam-steps/components/filter/ExamStepsListTop";
import {
  TExamStepsFilterFormExtended,
  defaultExamStepsFilterValues,
  isEmptyExamStepsFilterValue,
  normalizeExamStepsDateFields,
  normalizeExamStepsLegacyFields,
  stripEmptyExamStepsFilterFields,
} from "@/features/exam-steps/components/filter/exam-steps-filter-shared";
import { EXAM_STEPS_ADVANCED_FILTERS_STORAGE_KEY } from "@/features/exam-steps/constants/filter";
import { parseIndustryScopedFilterMap } from "@/utils/industry-scoped-filter-storage";

// OUT_OF_SCOPE learners are hidden by default — only included when the user explicitly
// selects that status in the filter. When no procedure-status filter is set, we inject
// every other status so BE excludes OUT_OF_SCOPE.
const DEFAULT_VISIBLE_PROCEDURE_STATUSES: string[] = [
  PROCEDURE_STATUS.NEW_REGISTRATION,
  PROCEDURE_STATUS.IN_PROGRESS,
  PROCEDURE_STATUS.COMPLETED,
];

const buildLearnerFilterParams = (filters: TExamStepsFilterFormExtended | null): TExamLearnersFilterParams => {
  const procedureStatuses =
    filters && filters.procedureStatuses.length > 0 ? filters.procedureStatuses : DEFAULT_VISIBLE_PROCEDURE_STATUSES;

  if (!filters) return { procedureStatuses };
  return {
    procedureStatuses,
    ...(filters.fullName ? { fullName: filters.fullName } : {}),
    ...(filters.rsoCompanyIds.length > 0 ? { rsoCompanyIds: filters.rsoCompanyIds } : {}),
    ...(filters.nationalityIds.length > 0 ? { nationalityIds: filters.nationalityIds } : {}),
    ...(filters.nextProcedureEndDateFrom
      ? { nextProcedureEndDateFrom: dayjs(filters.nextProcedureEndDateFrom).format("YYYY-MM-DD") }
      : {}),
    ...(filters.nextProcedureEndDateTo
      ? { nextProcedureEndDateTo: dayjs(filters.nextProcedureEndDateTo).format("YYYY-MM-DD") }
      : {}),
    ...(filters.examMonthFrom ? { examMonthFrom: dayjs(filters.examMonthFrom).format("YYYY/MM") } : {}),
    ...(filters.examMonthTo ? { examMonthTo: dayjs(filters.examMonthTo).format("YYYY/MM") } : {}),
    ...(filters.registeredAtFrom ? { registeredAtFrom: dayjs(filters.registeredAtFrom).format("YYYY-MM-DD") } : {}),
    ...(filters.registeredAtTo ? { registeredAtTo: dayjs(filters.registeredAtTo).format("YYYY-MM-DD") } : {}),
  };
};

export const ExamStepDetailPage: FC = () => {
  const { industryId } = useParams<{ industryId: string }>();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const pagReq = usePagination();

  const numericIndustryId = industryId ? Number(industryId) : 0;
  const currentPage = pagReq.page || START_PAGE;
  const perPage = pagReq.perPage || DEFAULT_PER_PAGE;

  const [advancedFilters, setAdvancedFilters] = useState<TExamStepsFilterFormExtended | null>(() => {
    const map = parseIndustryScopedFilterMap(localStorage.getItem(EXAM_STEPS_ADVANCED_FILTERS_STORAGE_KEY));
    const perIndustry = map[String(numericIndustryId)];
    if (!perIndustry) return null;
    return {
      ...defaultExamStepsFilterValues,
      ...(normalizeExamStepsLegacyFields(
        normalizeExamStepsDateFields(perIndustry as Partial<TExamStepsFilterFormExtended>) as Partial<Record<string, unknown>>,
      ) as Partial<TExamStepsFilterFormExtended>),
    } as TExamStepsFilterFormExtended;
  });

  const { data: industriesData } = useExamStepIndustries();
  const industry = industriesData?.data?.find((item) => item.id === numericIndustryId);
  const examRequirements = industry?.examRequirements ?? [];

  const { data: examsData, isLoading: isLoadingExams } = useLearningProgressExamList({ industryId: numericIndustryId });
  const exams = examsData?.data ?? [];

  const resetToFirstPage = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "1");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handleApplyAdvancedFilters = useCallback(
    (filters: TExamStepsFilterFormExtended) => {
      const hasAny = Object.values(filters).some((v) => !isEmptyExamStepsFilterValue(v));
      const map = parseIndustryScopedFilterMap(localStorage.getItem(EXAM_STEPS_ADVANCED_FILTERS_STORAGE_KEY));
      const industryKey = String(numericIndustryId);
      if (hasAny) {
        setAdvancedFilters(filters);
        map[industryKey] = stripEmptyExamStepsFilterFields(filters);
      } else {
        setAdvancedFilters(null);
        delete map[industryKey];
      }
      if (Object.keys(map).length > 0) {
        localStorage.setItem(EXAM_STEPS_ADVANCED_FILTERS_STORAGE_KEY, JSON.stringify(map));
      } else {
        localStorage.removeItem(EXAM_STEPS_ADVANCED_FILTERS_STORAGE_KEY);
      }
      resetToFirstPage();
    },
    [resetToFirstPage, numericIndustryId],
  );

  const { data: learnersData, isLoading: isLoadingLearners } = useExamStepLearners({
    industryId: numericIndustryId,
    page: currentPage,
    perPage,
    sorts: pagReq.sorts,
    ...buildLearnerFilterParams(advancedFilters),
  });

  const learners = learnersData?.data ?? [];
  const pagination = learnersData?.pagination ?? {
    page: currentPage,
    perPage,
    total: 0,
    totalPage: 0,
    nextPage: null,
    prevPage: null,
    count: 0,
  };

  const handleGoBackToExamStepList = useCallback(() => {
    navigate(AppRoutes.examSteps);
  }, [navigate]);

  return (
    <Stack gap={3}>
      <Typography variant="head20Bold">
        受験手続の進捗管理{industry?.name && ` - ${industry.name}`}
      </Typography>

      <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high} loading={isLoadingExams}>
        <ExamSummarySection exams={exams} industryId={numericIndustryId} />
      </InnerBox>

      <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high}>
        <Stack spacing={2}>
          <ExamStepsListTop
            pagination={pagination}
            onApplyAdvancedFilters={handleApplyAdvancedFilters}
            currentAdvancedFilters={advancedFilters}
            industryId={numericIndustryId}
          />
          <Stack spacing={3}>
            <ExamLearnerTable industryId={numericIndustryId} rows={learners} examRequirements={examRequirements} isLoading={isLoadingLearners} />
            <Pagination totalPage={pagination.totalPage} />
          </Stack>
        </Stack>
      </InnerBox>

      <FooterContainer>
        <Button size="sm" variant="ghost" onClick={handleGoBackToExamStepList}>
          戻る
        </Button>
      </FooterContainer>
    </Stack>
  );
};
